import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Grid,
	Card,
	CardContent,
	Typography,
	Box,
	CircularProgress,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BalanceIcon from "@mui/icons-material/Balance";
import MapIcon from "@mui/icons-material/Map";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import AccountBalanceWallet from "@mui/icons-material/AccountBalanceWallet";
import axios from "axios";
import { useUser } from "../contexts/UserContext";
import config from "../config.json";

// Helper to get clean headers for axios

const Dashboard = () => {
	const navigate = useNavigate();
	const { userCustomerId, userId } = useUser();
	const [data, setData] = useState(null);
	const [plansCount, setPlansCount] = useState(0);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!userId) {
			handleLogout();
		}
	}, [userId, navigate]);

	const handleLogout = () => {
		localStorage.removeItem("authToken");
		localStorage.removeItem("userData");
		navigate("/login");
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				if (!userCustomerId) return;

				const response = await axios.get(
					`${config.baseURL}middlewareCustomers/${userCustomerId}`,
					{
						headers: {
							...config.headers,
						},
					}
				);

				console.log("Dashboard data response:", response.data);
				var result = response.data[0];

				const processedData = {
					sourceConnectors: result?.Source_List?.length || 0,
					targetConnectors: result?.Target_List?.length || 0,
					transactionsPurchased: result?.Total_Transactions_Purchased || 0,
					transactionsConsumed: 0,
					balanceTransactions: (result?.Total_Transactions_Purchased || 0) - 0,
				};

				setData(processedData);
			} catch (error) {
				console.error("Error fetching dashboard data:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [userCustomerId]);

	useEffect(() => {
		const fetchPlansCounts = async () => {
			try {
				if (!userId) return;

				const planResponse = await axios.get(
					`${config.baseURL}middlewarePlans`,
					{
						headers: {
							...config.headers,
						},
					}
				);

				console.log("Plan response:", planResponse);
				console.log("Full planResponse.data:", planResponse.data);

				// Try to extract plans array from possible locations
				let plansArray = [];
				if (Array.isArray(planResponse.data)) {
					plansArray = planResponse.data;
				} else if (Array.isArray(planResponse.data.data)) {
					plansArray = planResponse.data.data;
				} else if (
					planResponse.data &&
					Array.isArray(planResponse.data.results)
				) {
					plansArray = planResponse.data.results;
				}

				// Filter plans where the semicolon-separated user_id contains the current userId
				const userIdStr = String(userId);
				plansArray = plansArray.filter((plan) => {
					if (!plan.user_id) return false;
					const ids = String(plan.user_id).split(";").map((id) => id.trim());
					return ids.includes(userIdStr);
				});

				const planIds = plansArray.map((plan) => plan.id);
				console.log("plansArray:", plansArray);
				console.log("Number of plans:", planIds.length);

				let plansCount = planIds.length;
				setPlansCount(plansCount);
				// let mappingsCount = 0;

				// if (plansCount > 0) {
				// 	const mappingResponse = await axios.get(
				// 		`${config.baseURL}middlewarePlanmappings`,
				// 		{
				// 			headers: {
				// 				...config.headers,
				// 			},
				// 			params: {
				// 				plan_id: planIds.join(","),
				// 			},
				// 		}
				// 	);

				// 	const mappingsArray = Array.isArray(mappingResponse.data)
				// 		? mappingResponse.data
				// 		: mappingResponse.data.data || [];
				// 	mappingsCount = mappingsArray.length;
				// }

				// setData((prevData) => ({
				// 	...prevData,
				// 	plans: plansCount,
				// 	mappings: mappingsCount,
				// }));
			} catch (error) {
				console.error("Error fetching mapping counts:", error);
			}
		};

		fetchPlansCounts();
	}, [userId]);

	useEffect(() => {
		const fetchTransactionsConsumed = async () => {
			try {
				if (!userId) return;
				const response = await axios.get(
					`${config.baseURL}middleware-consumed/${userId}`,
					{
						headers: {
							...config.headers,
						},
					}
				);
				const count = response.data?.count || 0;
				setData((prev) => ({
					...prev,
					transactionsConsumed: count,
					balanceTransactions: (prev?.transactionsPurchased || 0) - count,
				}));
			} catch (error) {
				console.error("Error fetching transactions consumed:", error);
			}
		};
		fetchTransactionsConsumed();
	}, [userId]);

	if (loading) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				height="100vh"
			>
				<CircularProgress />
			</Box>
		);
	}

	if (!data) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				height="100vh"
			>
				<Typography variant="h6" color="error">
					Failed to load dashboard data.
				</Typography>
			</Box>
		);
	}

	const tiles = [
		{
			title: "# of Source Connectors",
			value: data.sourceConnectors,
			icon: <AccountTreeIcon fontSize="large" />,
		},
		{
			title: "# of Target Connectors",
			value: data.targetConnectors,
			icon: <AccountTreeIcon fontSize="large" />,
		},
		{
			title: "# of Plans",
			value: plansCount || 0,
			icon: <LibraryBooksIcon fontSize="large" />,
		},

		{
			title: "Total Transactions Purchased",
			value: data.transactionsPurchased,
			icon: <ShoppingCartIcon fontSize="large" />,
		},
		{
			title: "Total Consumed",
			value: data.transactionsConsumed,
			icon: <TrendingUpIcon fontSize="large" />,
		},
		{
			title: "Balance Transactions",
			value: data.balanceTransactions,
			icon: <AccountBalanceWallet fontSize="large" />,
		},
	];

	return (
		<Box p={3}>
			<Grid container spacing={3}>
				{tiles.map((tile, index) => (
					<Grid item xs={12} sm={6} md={4} key={index}>
						<Card
							sx={{
								border: "1px solid #ddd",
								borderRadius: "16px",
								height: "150px",
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center",
								boxShadow: "none",
								paddingTop: "16px",
								transition: "transform 0.3s ease",
								"&:hover": {
									transform: "scale(1.05)",
								},
							}}
						>
							<CardContent>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
									}}
								>
									<Box
										sx={{
											background: "rgba(0, 0, 0, 0.05)",
											borderRadius: "50%",
											padding: "12px",
											marginBottom: "12px",
										}}
									>
										{tile.icon}
									</Box>
									<Typography
										variant="subtitle1"
										fontWeight="bold"
										gutterBottom
									>
										{tile.title}
									</Typography>
									<Typography variant="h5" fontWeight="bold">
										{tile.value}
									</Typography>
								</Box>
							</CardContent>
						</Card>
					</Grid>
				))}
			</Grid>
		</Box>
	);
};

export default Dashboard;
